import React, { useState } from "react";
import { intl } from "LocaleWrapper";

import { Button, FormGroup, LabelPlacement } from "@cofano/cck";
import { FormGroupOverflow } from "@cofano/cck/FormGroup/utils";

import { ProductReviewStatusTag } from "components/products/ProductTags";

import { DataFormat, Language, usePermission } from "utils";

import { productTankerTypeField } from "view/products/components/form/fields/ProductTankerType";
import { productTemperatureClassField } from "view/products/components/form/fields/ProductTemperatureClass";
import { CargoTankType, productCargoTankTypeField } from "view/products/components/form/fields/ProductCargoTankType";
import { CargoTankDesign, productCargoTankDesignField } from "view/products/components/form/fields/ProductCargoTankDesign";
import { CargoTankEquipment, productCargoTankEquipmentField } from "view/products/components/form/fields/ProductCargoTankEquipment";
import { SamplingType, productSamplingTypeField } from "view/products/components/form/fields/ProductSamplingType";
import { productEquipmentField } from "view/products/components/form/fields/ProductEquipment";
import ProductReviewDialog from "view/products/components/form/ProductReviewDialog";
import { headerMap } from "view/products/utils/ProductUtils";
import { Product } from "view/products/utils/product.types";

const ProductDangerousGoods = ({
	product,
	refetch,
}: {
	product: Product,
	refetch: () => void;
}) => {

	const { isReadOnly, isRTR } = usePermission();

	const [ adnReviewDialogOpen, setAdnReviewDialogOpen ] = useState(false);

	const { adnAttributes } = product;

	const showDangerousGoodsReview = !isReadOnly && isRTR && product.isDangerousGood && product.adnGood != null;

	const getDensity = () => {
		if (product.density) {
			return DataFormat.simpleUnit(Number(product.density), DataFormat.UNITS.DENSITY);
		} else if (product.densityKgM3Min && product.densityKgM3Max) {
			return `${DataFormat.simpleUnit(product.densityKgM3Min, DataFormat.UNITS.DENSITY)} - ${DataFormat.simpleUnit(product.densityKgM3Max, DataFormat.UNITS.DENSITY)}`;
		} else if (product.densityKgM3Min) {
			return `> ${DataFormat.simpleUnit(product.densityKgM3Min, DataFormat.UNITS.DENSITY)}`;
		} else if (product.densityKgM3Max) {
			return `< ${DataFormat.simpleUnit(product.densityKgM3Max, DataFormat.UNITS.DENSITY)}`;
		} else {
			return "-";
		}
	};

	const optionalFormGroupProps = {
		overflow: "visible" as FormGroupOverflow,
		labelPlacement: "left-center" as LabelPlacement,
		labelWidth: 300,
	};

	return (
		<div>
			{!product.isDangerousGood ? (
				<React.Fragment>
					"No dangerous good"
				</React.Fragment>
			) : (
				<React.Fragment>
					{showDangerousGoodsReview && (
						<div style={{ alignItems: "center", display: "flex", justifyContent: "end", marginBottom: 16 }}>
							{product.needsManualReview && (
								<ProductReviewStatusTag product={product} />
							)}

							<Button
								onClick={() => setAdnReviewDialogOpen(true)}
								style={{ marginLeft: 8 }}
								size="small"
								variant="outlined">
								{intl.formatMessage({ id: "REVIEW_ADN" })}
							</Button>
						</div>
					)}

					<FormGroup label={Language("PRODUCT_ADN_ADN_CODE")} {...optionalFormGroupProps}>
						{adnAttributes?.adnCode || "-"}
					</FormGroup>

					<FormGroup label={headerMap["unNumber"]} {...optionalFormGroupProps}>
						{adnAttributes?.unNumber || "-"}
					</FormGroup>

					<FormGroup label={headerMap["properShippingNameEN"]} {...optionalFormGroupProps}>
						{product.properShippingNameEN || "-"}
					</FormGroup>

					<FormGroup label={headerMap["properShippingNameNL"]} {...optionalFormGroupProps}>
						{product.properShippingNameNL || "-"}
					</FormGroup>

					<FormGroup label={headerMap["properShippingNameDE"]} {...optionalFormGroupProps}>
						{product.properShippingNameDE || "-"}
					</FormGroup>

					<FormGroup label={headerMap["properShippingNameFR"]} {...optionalFormGroupProps}>
						{product.properShippingNameFR || "-"}
					</FormGroup>

					<FormGroup label={headerMap["classNumber"]} {...optionalFormGroupProps}>
						{adnAttributes?.classNumber || "-"}
					</FormGroup>

					<FormGroup label={headerMap["classificationCode"]} {...optionalFormGroupProps}>
						{adnAttributes?.classification || "-"}
					</FormGroup>

					<FormGroup label={headerMap["packingGroup"]} {...optionalFormGroupProps}>
						{adnAttributes?.packingGroup || "-"}
					</FormGroup>

					<FormGroup label={headerMap["hazards"]} {...optionalFormGroupProps}>
						{adnAttributes?.hazardCode || "-"}
					</FormGroup>

					<FormGroup label={headerMap["tankerType"]} {...optionalFormGroupProps}>
						{productTankerTypeField(adnAttributes?.tankerType)}
					</FormGroup>

					<FormGroup label={headerMap["cargoTankDesign"]} {...optionalFormGroupProps}>
						{productCargoTankDesignField(adnAttributes?.cargoTankDesign as keyof typeof CargoTankDesign)}
					</FormGroup>

					<FormGroup label={headerMap["cargoTankType"]} {...optionalFormGroupProps}>
						{productCargoTankTypeField(adnAttributes?.cargoTankType as keyof typeof CargoTankType)}
					</FormGroup>

					<FormGroup label={headerMap["cargoTankEquipment"]} {...optionalFormGroupProps}>
						{productCargoTankEquipmentField(adnAttributes?.cargoTankEquipment as keyof typeof CargoTankEquipment)}
					</FormGroup>

					<FormGroup label={headerMap["openingPressureRelieveValve"]} {...optionalFormGroupProps}>
						{adnAttributes?.openingPressureRelieveValve || "-"}
					</FormGroup>

					<FormGroup label={headerMap["maxFillingDegreePercentage"]} {...optionalFormGroupProps}>
						{adnAttributes?.maxFillingDegreePercentage || "-"}
					</FormGroup>

					<FormGroup label={headerMap["density"]} {...optionalFormGroupProps}>
						{getDensity()}
					</FormGroup>

					<FormGroup label={headerMap["densityTemperature"]} {...optionalFormGroupProps}>
						{product.densityTemperature ? `${product.densityTemperature}°C` : "-"}
					</FormGroup>

					<FormGroup label={headerMap["samplingType"]} {...optionalFormGroupProps}>
						{productSamplingTypeField(adnAttributes?.samplingType as keyof typeof SamplingType)}
					</FormGroup>

					<FormGroup label={headerMap["permissiblePumpChamberUnderDeck"]} {...optionalFormGroupProps}>
						{DataFormat.booleanAsText(adnAttributes?.permissiblePumpChamberUnderDeck) || "-"}
					</FormGroup>

					<FormGroup label={headerMap["temperatureClass"]} {...optionalFormGroupProps}>
						{productTemperatureClassField(adnAttributes?.temperatureClass)}
					</FormGroup>

					<FormGroup label={headerMap["explosionGroup"]} {...optionalFormGroupProps}>
						{adnAttributes?.explosionGroup || "-"}
					</FormGroup>

					<FormGroup label={headerMap["explosionProtectionRequired"]} {...optionalFormGroupProps}>
						{DataFormat.booleanAsText(adnAttributes?.explosionProtectionRequired) || "-"}
					</FormGroup>

					<FormGroup label={headerMap["requiredEquipment"]} {...optionalFormGroupProps}>
						{productEquipmentField(adnAttributes?.requiredEquipment)}
					</FormGroup>

					<FormGroup label={headerMap["numberOfBlueConesOrLights"]} {...optionalFormGroupProps}>
						{adnAttributes?.numberOfBlueConesOrLights || "-"}
					</FormGroup>

					<FormGroup label={headerMap["extraRequirements"]} {...optionalFormGroupProps}>
						{adnAttributes?.extraRequirements || "-"}
					</FormGroup>
				</React.Fragment>
			)}

			{product.adnGood && (
				<ProductReviewDialog
					refetch={refetch}
					product={product}
					open={adnReviewDialogOpen}
					onClose={() => setAdnReviewDialogOpen(false)} />
			)}
		</div>
	);
};

export default ProductDangerousGoods;
